/**
 * Logique pure du panneau de recherche globale, extraite de NavBar pour
 * rester testable sans rendu (le depot n'a pas @testing-library).
 */
import i18n from '../../i18n';

/**
 * Quatre tons, quatre gestes, qui ne doivent jamais se lire pareil.
 *
 * 'erreur' : la recherche elle-meme n'a pas abouti (panne reseau, 500,
 * serveur injoignable). Ce n'est pas un manque partiel constate par le
 * serveur, c'est l'absence de reponse exploitable : sans ce ton, une panne de
 * transport se confond avec une reponse vide legitime, et l'agent conclut
 * que le dossier n'existe pas alors qu'on n'a simplement pas pu chercher.
 *
 * 'reessayer' (typesIndisponibles) : le type n'a pas repondu, a ete trop
 * lent ou est en panne. Reessayer peut marcher.
 *
 * 'demander' (typesNonAutorises) : le type n'a pas ete interroge du tout,
 * faute d'habilitation. Reessayer ne changera rien, il faut demander le
 * droit nomme dans habilitationRequise. Une seule mention resume le nombre
 * de types concernes (ex. "7 types hors de vos droits") ; le detail par
 * type (nomme avec l'habilitation a demander, sur demande explicite de
 * l'utilisateur) vit dans `details`, que l'ecran deplie a la demande plutot
 * que de l'empiler d'office. Un agent peu habilite (GESTIONNAIRE, COURSIER :
 * 1 habilitation sur 8, donc 7 types non autorises a chaque frappe) recevait
 * sinon 7 lignes ambre sous chaque resultat, a chaque caractere tape :
 * l'information reste disponible, seule sa mise en page change.
 *
 * 'plafond' : un type a rendu exactement `limiteParType` resultats. Le
 * serveur plafonne silencieusement ; sans cette mention, un prefixe qui
 * correspond a 9 dossiers en affiche 5 sans un mot sur les 4 restants, et
 * l'agent croit avoir vu tous les resultats.
 *
 * Confondre reessayer et demander ferait reessayer indefiniment sur un
 * refus de droits.
 *
 * @param reponse la reponse du service de recherche. `reponse.erreur` vrai
 *   signale un echec de transport plutot qu'une reponse serveur normale ;
 *   NavBar pose ce champ lui-meme, le serveur ne le renvoie jamais.
 * @param options.limiteParType la limite par type transmise au serveur pour
 *   cette recherche (voir NavBar : LIMITE_PAR_TYPE). Omise, aucune mention
 *   'plafond' n'est produite : mieux vaut se taire que deviner une limite
 *   qui n'a pas ete demandee.
 * @returns tableau de { ton, texte, details? }. Une reponse sans manque rend
 *   un tableau vide.
 */
export function mentionsRecherche(reponse, { limiteParType } = {}) {
  if (!reponse) return [];
  const mentions = [];

  if (reponse.erreur) {
    mentions.push({
      ton: 'erreur',
      texte: i18n.t('commun:panneauRecherche.erreur'),
    });
  }

  const indisponibles = reponse.typesIndisponibles || [];
  const nonAutorises = reponse.typesNonAutorises || [];

  if (indisponibles.length > 0) {
    mentions.push({
      ton: 'reessayer',
      texte: i18n.t('commun:panneauRecherche.indisponibles', { types: indisponibles.join(', ') }),
    });
  }

  if (nonAutorises.length > 0) {
    mentions.push({
      ton: 'demander',
      texte: i18n.t('commun:panneauRecherche.nonAutorises', { count: nonAutorises.length }),
      // Deplie a la demande : le detail par type + habilitation reste
      // disponible (l'utilisateur l'a explicitement demande), simplement
      // pas impose d'office a chaque frappe.
      details: nonAutorises.map((na) => ({
        type: na.type,
        texte: i18n.t('commun:panneauRecherche.pasAcces', { type: na.type, habilitation: na.habilitationRequise }),
      })),
    });
  }

  if (limiteParType) {
    const resultats = reponse.resultats || [];
    const comptesParType = {};
    resultats.forEach((r) => {
      comptesParType[r.type] = (comptesParType[r.type] || 0) + 1;
    });
    const typesAuPlafond = Object.keys(comptesParType)
      .filter((type) => comptesParType[type] === limiteParType);
    if (typesAuPlafond.length > 0) {
      mentions.push({
        ton: 'plafond',
        texte: i18n.t('commun:panneauRecherche.plafond', { types: typesAuPlafond.join(', ') }),
      });
    }
  }

  return mentions;
}

/**
 * Route d'un resultat, telle que le serveur la porte.
 *
 * JAMAIS une URL construite a partir du type et d'un identifiant : c'est
 * precisement ce que le contrat serveur existe pour eviter. Un neuvieme type
 * ajoute demain ferait mentir un aiguillage par type sans qu'aucun test ne
 * s'en apercoive ; rendre `resultat.route` tel quel n'a pas ce probleme.
 */
export function routeDuResultat(resultat) {
  return resultat?.route ?? null;
}

/**
 * Destination reelle de la route d'un resultat : 'fiche' quand la route
 * adresse directement un dossier, 'liste' quand elle depose sur l'ecran de
 * liste du type, non filtre par ce clic.
 *
 * Trois types sur huit (SINISTRE, RECLAMATION, ATTESTATION) ont une vraie
 * fiche ; les cinq autres (PV, COURRIER, BORDEREAU, ENCAISSEMENT, REGLEMENT)
 * n'ont qu'un ecran de liste dans cette application, et leur route porte le
 * numero cherche en parametre `?recherche=` qu'aucun ecran de ce depot ne
 * lit -- verifie le 2026-09-25 (`grep -rn "get('recherche')" src` ne rend
 * rien) : le clic atterrit donc sur la page 1, non filtree, sans que rien ne
 * le distingue d'un clic rate.
 *
 * JAMAIS deduit d'une liste de types ecrite en dur ici : c'est precisement
 * ce que ce chantier veut eviter (voir routeDuResultat plus haut). Le signe
 * est la route elle-meme, via le parametre `recherche` qu'elle porte ou non
 * -- un neuvieme type sans fiche, demain, porterait la meme marque et
 * serait reconnu sans qu'on ait besoin de revenir modifier cette fonction.
 *
 * @returns 'fiche' | 'liste' | null (pas de route du tout : rien a ouvrir)
 */
export function destinationRoute(resultat) {
  const route = routeDuResultat(resultat);
  if (!route) return null;
  const indexInterrogation = route.indexOf('?');
  if (indexInterrogation === -1) return 'fiche';
  const params = new URLSearchParams(route.slice(indexInterrogation + 1));
  return params.has('recherche') ? 'liste' : 'fiche';
}
